import React, { Component } from "react";
import PropTypes from "prop-types";
import {
  mfmFramedUrl,
  mfmVariantUrl,
  getFileFraming,
  getFileVariants,
} from "../../Utils/MfmService";
import "./MfmPhoto.css";

/**
 * MfmPhoto — Clon de la lógica de visualización de MfmImageUploader, sin opciones
 * de subida ni modal. Replica exactamente cómo MfmImageUploader muestra la imagen
 * con el encuadre guardado por el usuario.
 *
 * Estrategia (idéntica a MfmImageUploader.syncPersistedVariantFromServer):
 *   1. Al montar, lanza en paralelo getFileFraming + getFileVariants.
 *   2. Construye la clave exacta de la variante persistida.
 *   3. Si existe → usa mfmVariantUrl (URL directa, cache inmutable, sin generación).
 *   4. Si no existe → usa mfmFramedUrl como fallback (backend genera on-demand).
 *
 * Props (todos opcionales salvo uuid):
 *  - uuid        {string}   UUID del fichero MFM. Si es null/vacío muestra `fallback`.
 *  - formatKey   {string}   Clave de formato (ej: "ratio:3:4"). Si se omite, busca
 *                           variante encuadrada por dimensiones.
 *  - width       {number}   Ancho en px. Opcional (con formatKey puede omitirse).
 *  - height      {number}   Alto en px. Opcional.
 *  - fallback    {string}   URL de imagen a mostrar cuando no hay uuid o imagen.
 *  - alt         {string}   Atributo alt del <img>.
 *  - className   {string}   Clase CSS adicional.
 *  - style       {object}   Estilos inline adicionales.
 *  - onClick     {function} Handler de click.
 */
export class MfmPhoto extends Component {
  _reqId = 0;

  state = {
    variantUrl: null,
    resolvedUuid: null,
  };

  componentDidMount() {
    this.syncVariant();
  }

  componentDidUpdate(prevProps) {
    const { uuid, formatKey, width, height } = this.props;
    if (
      prevProps.uuid !== uuid ||
      prevProps.formatKey !== formatKey ||
      prevProps.width !== width ||
      prevProps.height !== height
    ) {
      this.setState({ variantUrl: null, resolvedUuid: null });
      this.syncVariant();
    }
  }

  componentWillUnmount() {
    this._reqId++;
  }

  buildFramedVariantKey(formatKey, width, height, framing) {
    if (!formatKey || !width || !height || !framing) return null;
    const x = Number(framing.x).toFixed(3);
    const y = Number(framing.y).toFixed(3);
    const z = Number(framing.zoom).toFixed(3);
    return `framed:${formatKey}:${Math.round(width)}x${Math.round(height)}:x${x}:y${y}:z${z}`;
  }

  syncVariant = async () => {
    const { uuid, formatKey, width, height } = this.props;
    if (!uuid) return;

    const reqId = ++this._reqId;
    try {
      if (formatKey) {
        // Camino óptimo: framing + variantes en paralelo (igual que MfmImageUploader)
        const [framing, variants] = await Promise.all([
          getFileFraming(uuid, formatKey),
          getFileVariants(uuid),
        ]);

        if (reqId !== this._reqId) return;
        if (!Array.isArray(variants)) return;

        let matched = null;
        if (framing && framing.x != null && width && height) {
          const expectedKey = this.buildFramedVariantKey(formatKey, width, height, framing);
          matched =
            variants.find((v) => String(v?.variantKey || "") === expectedKey) ||
            variants.find((v) =>
              String(v?.variantKey || "").startsWith(
                `framed:${formatKey}:${Math.round(width)}x${Math.round(height)}:`,
              ),
            ) ||
            variants.find(
              (v) =>
                v?.variantKey?.startsWith("framed:") &&
                Math.round(v.width) === Math.round(width) &&
                Math.round(v.height) === Math.round(height),
            );
        } else {
          // Sin dimensiones exactas: primera variante encuadrada del formato
          matched = variants.find((v) =>
            String(v?.variantKey || "").startsWith(`framed:${formatKey}:`),
          );
        }

        if (matched?.variantKey) {
          this.setState({
            variantUrl: mfmVariantUrl(uuid, String(matched.variantKey)),
            resolvedUuid: uuid,
          });
        }
      } else if (width && height) {
        // Sin formatKey pero con dimensiones: buscar variante encuadrada por tamaño
        const variants = await getFileVariants(uuid);
        if (reqId !== this._reqId) return;
        if (!Array.isArray(variants)) return;

        const matched = variants.find(
          (v) =>
            v?.variantKey?.startsWith("framed:") &&
            Math.round(v.width) === Math.round(width) &&
            Math.round(v.height) === Math.round(height),
        );

        if (matched?.variantKey) {
          this.setState({
            variantUrl: mfmVariantUrl(uuid, String(matched.variantKey)),
            resolvedUuid: uuid,
          });
        }
      }
    } catch {
      // Error de red / permisos: el fallback mfmFramedUrl del render lo cubre
    }
  };

  handleError = (e) => {
    const { fallback } = this.props;
    if (fallback && e?.currentTarget?.src !== fallback) {
      e.currentTarget.src = fallback;
    }
  };

  render() {
    const { uuid, width, height, formatKey, fallback, alt, className, style, onClick } = this.props;
    const { variantUrl, resolvedUuid } = this.state;

    let src;
    if (!uuid) {
      src = fallback || null;
    } else if (variantUrl && resolvedUuid === uuid) {
      // Variante exacta encontrada: URL directa, cache inmutable
      src = variantUrl;
    } else {
      // Fallback: endpoint dinámico (backend aplica el encuadre on-demand)
      src = width && height ? mfmFramedUrl(uuid, width, height, formatKey) : null;
      if (!src) src = fallback || null;
    }

    if (!src) return null;

    const aspectRatio = width && height ? `${width} / ${height}` : undefined;

    return (
      <img
        src={src}
        alt={alt || ""}
        className={["mfm-photo", className].filter(Boolean).join(" ")}
        style={{ aspectRatio, ...style }}
        onError={this.handleError}
        onClick={onClick}
      />
    );
  }
}

MfmPhoto.defaultProps = {
  formatKey: undefined,
  fallback: undefined,
  alt: "",
  className: undefined,
  style: undefined,
  onClick: undefined,
};

MfmPhoto.propTypes = {
  uuid: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  formatKey: PropTypes.string,
  fallback: PropTypes.string,
  alt: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  onClick: PropTypes.func,
};

export default MfmPhoto;
